import React from "react";
import { Award, PartyPopper } from "lucide-react";
import AvatarImage from "./AvatarImage";
import Tooltip from "../Tooltip/Tooltip";
import {
  EmployeeCelebration,
  useEmployeeMilestones,
} from "../../../contexts/EmployeeMilestoneContext";

export type MilestoneAvatarSize =
  | "xxxs"
  | "xxs"
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl";

export type MilestoneAvatarLabelMode = "none" | "compact" | "full";

interface MilestoneAvatarProps {
  userId: string;
  displayName?: string;
  size?: MilestoneAvatarSize;
  className?: string;
  alternate?: boolean;
  labelMode?: MilestoneAvatarLabelMode;
  date?: Date;
  disableMilestones?: boolean;
  tooltipContent?: React.ReactNode;
  tooltipTheme?: "light" | "dark";
}

const labelTextClass: Record<MilestoneAvatarSize, string> = {
  xxxs: "text-[10px]",
  xxs: "text-[10px]",
  xs: "text-[11px]",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm",
  xl: "text-base",
};

const tabClass: Record<MilestoneAvatarSize, string> = {
  xxxs: "h-4 min-w-4 px-0.5 -right-1 -top-1",
  xxs: "h-[18px] min-w-[18px] px-1 -right-1.5 -top-1.5",
  xs: "h-5 min-w-5 px-1 -right-1.5 -top-1.5",
  sm: "h-[22px] min-w-[22px] px-1.5 -right-2 -top-1.5",
  md: "h-6 min-w-6 px-1.5 -right-2 -top-2",
  lg: "h-7 min-w-7 px-2 -right-2.5 -top-2",
  xl: "h-8 min-w-8 px-2 -right-3 -top-2.5",
};

const iconSize: Record<MilestoneAvatarSize, number> = {
  xxxs: 9,
  xxs: 10,
  xs: 11,
  sm: 12,
  md: 13,
  lg: 15,
  xl: 17,
};

const emojiSizeClass: Record<MilestoneAvatarSize, string> = {
  xxxs: "text-[9px]",
  xxs: "text-[10px]",
  xs: "text-[11px]",
  sm: "text-xs",
  md: "text-[13px]",
  lg: "text-sm",
  xl: "text-base",
};

const showAnniversaryNumber = (size: MilestoneAvatarSize): boolean =>
  !["xxxs", "xxs"].includes(size);

const MilestoneTooltipContent: React.FC<{
  celebration: EmployeeCelebration;
  theme?: "light" | "dark";
}> = ({ celebration, theme = "light" }) => {
  const isBirthday =
    celebration.primaryType === "BIRTHDAY" ||
    celebration.primaryType === "MULTIPLE";

  const isAnniversary =
    celebration.primaryType === "WORK_ANNIVERSARY" ||
    celebration.primaryType === "MULTIPLE";

  return (
    <div className="min-w-[160px] max-w-[210px] py-0.5">
      <div className={`truncate text-[11px] font-semibold ${theme === "dark" ? "text-zinc-400" : "text-gray-500"}`}>
        {celebration.displayName}
      </div>

      <div className="mt-1 space-y-0.5">
        {isBirthday && (
          <div className={`text-[12px] font-semibold ${theme === "dark" ? "text-zinc-100" : "text-gray-900"}`}>
            🎂 Happy Birthday!
          </div>
        )}

        {isAnniversary && celebration.anniversary?.years ? (
          <div className={`text-[12px] font-semibold ${theme === "dark" ? "text-zinc-100" : "text-gray-900"}`}>
            Celebrating {celebration.anniversary.years} year
            {celebration.anniversary.years === 1 ? "" : "s"}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const MilestoneTab: React.FC<{
  celebration: EmployeeCelebration;
  size: MilestoneAvatarSize;
}> = ({ celebration, size }) => {
  const common =
    `absolute z-20 flex items-center justify-center gap-0.5 rounded-[9px] border-2 border-white ` +
    `font-semibold leading-none shadow-sm pointer-events-none ${tabClass[size]}`;

  if (celebration.primaryType === "BIRTHDAY") {
    return (
      <span
        className={`${common} bg-amber-100 text-amber-700 ${emojiSizeClass[size]}`}
        aria-hidden="true"
      >
        🎂
      </span>
    );
  }

  if (celebration.primaryType === "WORK_ANNIVERSARY") {
    const years = celebration.anniversary?.years;

    return (
      <span
        className={`${common} bg-emerald-100 text-emerald-700`}
        aria-hidden="true"
      >
        <Award size={iconSize[size]} strokeWidth={2.25} />
        {years && showAnniversaryNumber(size) ? (
          <span className="text-[9px] leading-none">{years}</span>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className={`${common} bg-violet-100 text-violet-700`}
      aria-hidden="true"
    >
      <PartyPopper size={iconSize[size]} strokeWidth={2.25} />
    </span>
  );
};

const MilestoneAvatar: React.FC<MilestoneAvatarProps> = ({
  userId,
  displayName,
  size = "md",
  className,
  alternate = true,
  labelMode = "none",
  date,
  disableMilestones = false,
  tooltipContent,
  tooltipTheme = "light",
}) => {
  const { getCelebrationForUser } = useEmployeeMilestones();

  const celebration = disableMilestones
    ? null
    : getCelebrationForUser(userId, date ?? new Date());

  const contextualLabel =
    celebration && labelMode !== "none"
      ? labelMode === "full"
        ? celebration.fullLabel
        : celebration.compactLabel
      : null;

  const resolvedTooltipContent = celebration
    ? <MilestoneTooltipContent celebration={celebration} theme={tooltipTheme} />
    : tooltipContent;

  const avatarNode = (
    <span className="relative inline-flex shrink-0 overflow-visible">
      <AvatarImage
        userId={userId}
        displayName={displayName}
        size={size}
        className={className}
        alternate={alternate}
      />

      {celebration ? (
        <MilestoneTab celebration={celebration} size={size} />
      ) : null}
    </span>
  );

  return (
    <span className="inline-flex min-w-0 items-center gap-2 align-middle">
      {resolvedTooltipContent ? (
        <Tooltip
          content={resolvedTooltipContent}
          position="bottom"
          theme={tooltipTheme}
        >
          {avatarNode}
        </Tooltip>
      ) : (
        avatarNode
      )}

      {contextualLabel ? (
        <span
          className={`min-w-0 truncate font-semibold text-slate-600 ${labelTextClass[size]}`}
        >
          {contextualLabel}
        </span>
      ) : null}
    </span>
  );
};

export default MilestoneAvatar;
